"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { HelpCircle, ArrowRight } from "lucide-react";

type Destination = { code: string; name: string; price?: string; gb?: number };

// market-test-plan.md §3 — homepage guided "help me choose" entry.
const EXPERIMENT_ID = "wf_homepage_help_me_choose_v1";

/**
 * WanderLite popular destinations — soft white rounded-3xl cards with a flag,
 * a friendly "Country – X GB" label and a from-price, plus a distinct
 * "Not sure where to go?" assist card with a speech-bubble icon and a
 * rounded-full "Help me choose" pill. Guidance-first, not a marketplace grid.
 *
 * Analytics: the assist pill fires `help_me_choose_started` (market-test §10)
 * carrying the §3 `experiment_id` on the shared AnalyticsEvent schema.
 */
export default function PopularDestinations({ destinations }: { destinations: Destination[] }) {
  const { t } = useTranslations();

  const onHelpMeChoose = () => {
    track({
      event_name: "help_me_choose_started",
      event_id: `help_me_choose_started:popular:${EXPERIMENT_ID}:${Date.now()}`,
      experiment_id: EXPERIMENT_ID,
    });
  };

  return (
    <section className="container mx-auto px-4 py-14">
      <Reveal as="h2" className="mb-8 text-3xl font-extrabold text-foreground sm:text-4xl" y={14}>
        {t("wander.popular.title", "Popular Destinations")}
      </Reveal>

      <Stagger
        as="div"
        className="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        staggerChildren={0.06}
      >
        {destinations.map((d) => {
          const code = d.code.toLowerCase();
          const label =
            d.gb != null
              ? t("wander.popular.cardLabel", "{name} – {gb} GB")
                  .replace("{name}", d.name)
                  .replace("{gb}", String(d.gb))
              : d.name;
          return (
            <StaggerItem as="div" key={d.code} y={12}>
              <Link
                href={`/destinations/${code}/`}
                className="wander-card group flex h-full flex-col items-center p-6 text-center transition-all hover:-translate-y-1 hover:shadow-[0_14px_30px_-16px_hsl(var(--primary)/0.55)]"
              >
                <img
                  src={`https://flagcdn.com/${code}.svg`}
                  alt=""
                  role="presentation"
                  width={64}
                  height={46}
                  className="h-12 w-16 rounded-lg object-cover shadow-sm transition-transform group-hover:scale-105"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                <div className="mt-4 font-semibold text-foreground">{label}</div>
                {d.price ? (
                  <div className="mt-1 text-lg font-bold text-primary">{d.price}</div>
                ) : null}
              </Link>
            </StaggerItem>
          );
        })}

        {/* Distinct "Not sure where to go?" assist card */}
        <StaggerItem as="div" y={12}>
          <div className="wander-card flex h-full flex-col items-center justify-center p-6 text-center ring-1 ring-primary/15">
            <span className="wander-chip inline-flex h-14 w-14 items-center justify-center">
              <HelpCircle className="h-7 w-7" />
            </span>
            <div className="mt-3 font-semibold text-foreground">
              {t("wander.popular.notSure", "Not sure where to go?")}
            </div>
            <Link
              href="/destinations/"
              onClick={onHelpMeChoose}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98] hover:bg-primary/90"
            >
              {t("wander.popular.helpMeChoose", "Help me choose")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </StaggerItem>
      </Stagger>
    </section>
  );
}
